import type { AxiosRequestConfig, AxiosInstance, CancelTokenSource } from 'axios';
import Axios from 'axios';
import type { FilesystemApis } from './fileOps';
import type { FileApi } from './fileApi';
import type { TaskPriority } from '../async/taskPriority';
import type { PrioritySemaphore } from '../async/prioritySemaphore';
import type { ReadableFile} from './readableFile';
import { makeReadableFile } from './readableFile';
import type { FileSetListener } from './uploadProgression';
import { OperationConclusion } from '../dto/progress';
import { ProgressBuilder } from '../async/progressBuilder';
import { UPLOAD_MULTIPART_THRESHOLD } from './limits';
import type { StructuredLogger } from '@mimir/primitive/structuredLogger';

export class SimpleUploader {
  private cancelTokenSource: CancelTokenSource;
  private rfile: ReadableFile;
  private progressBuilder: ProgressBuilder;

  constructor(private axios: AxiosInstance, private signedUrl: string, file: FileApi, readonly identifier: string,
                fsApis: FilesystemApis | undefined, private readonly fsSemaphore: PrioritySemaphore, listener: FileSetListener | null, private priority: TaskPriority, private structuredLogger: StructuredLogger) {
    this.cancelTokenSource = Axios.CancelToken.source();
    this.rfile = makeReadableFile(file, fsApis);
    if (this.rfile.size > UPLOAD_MULTIPART_THRESHOLD) {
      this.structuredLogger.log({
        level: 'warn',
        message: 'File too large for SimpleUploader',
        scope: 'SimpleUploader/constructor',
        size: this.rfile.size,
        UPLOAD_MULTIPART_THRESHOLD
      });
    }
    this.progressBuilder = new ProgressBuilder({
      files: {
        done: 0,
        known: 1,
      },
      bytes: {
        done: 0,
        known: this.rfile.size
      },
      status: 'Upload queued',
      description: 'Prepared ' + this.rfile.title + ' for upload',
      errors: [],
      percentage: 0,
      uploadMethods: { [identifier]: 'httpSinglePart' },
      conclusion: OperationConclusion.PENDING
    }, listener, this.cancelTokenSource.token);
  }
  public async startUpload() {
    this.cancelTokenSource.token.throwIfRequested();
    this.progressBuilder.fire();
    const buffer = await this.fsSemaphore.limit(async () => {
      try {
        return await this.readFile();
      } finally {
        // If using nodejs files we must close the handle
        await this.rfile.close();
      }
    }, this.priority, this.cancelTokenSource.token);
    await this.putBuffer(buffer);
  }

  private async readFile(): Promise<ArrayBuffer> {
    this.progressBuilder.fireActivity('Uploading file', 'Uploading ' + this.rfile.title);
    const cancelToken = this.cancelTokenSource.token;
    cancelToken.throwIfRequested();

    // Read next buffer to update either using premiere nodejs api or file.slice
    return this.rfile.getArrayBuffer(0, this.rfile.size, this.structuredLogger);
  }

  private async putBuffer(buffer: ArrayBuffer) {
    const cancelToken = this.cancelTokenSource.token;
    cancelToken.throwIfRequested();
    const config: AxiosRequestConfig = {
      cancelToken,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    };

    config.onUploadProgress = (progressEvent: { loaded: number }) => {
      this.progressBuilder.progress.bytes.done = progressEvent.loaded;
      this.progressBuilder.fire();
    };

    await this.axios.put(this.signedUrl, buffer, config);
  }
  public cancel() {
    this.cancelTokenSource.cancel(`SimpleUploader canceled ${this.rfile.title}`);
  }
}
